import { Button, ConfigProvider, DatePicker, Form, message, Modal } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import { WaterInvoiceModel } from "@/core/models/water-invoice.model";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { toApiTimestamp, DEF_DATETIME_FORMAT } from "@/app/home/utils/global.utils";
import { formatCurrencyPipeText } from "@/core/pipes/format-currency.pipe";

const waterInvoiceRepo = container.get(WaterInvoiceRepo);

interface UpdatePaymentDateModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: WaterInvoiceModel,
    onSuccess?: () => void;
}

const INPUT_WIDTH = 450;

export const UpdatePaymentDateModal: React.FC<UpdatePaymentDateModalProps> = ({
    isModalOpen, 
    setIsModalOpen, 
    data,
    onSuccess
}) => {
    const [form] = Form.useForm<{paymentDate?: any}>();
    const [loading, setLoading] = useState<boolean>(false);

    // Initialize form data when modal opens
    useEffect(() => {
        if (isModalOpen && data) {
            form.setFieldsValue({
                paymentDate: data.paymentDate
            });
        }
    }, [isModalOpen, data, form]);

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            const result = await waterInvoiceRepo.updateWaterInvoice({
                id: data.id,
                paymentDate: formData.paymentDate ? toApiTimestamp(formData.paymentDate) : undefined
            });
            
            if (result === true) {
                message.success({
                    content: "Cập nhật ngày nhận tiền thành công!"
                });
                setIsModalOpen(false);
                onSuccess?.();
                return;
            } else {
                message.error({
                    content: typeof result === "string" ? result : "Cập nhật ngày nhận tiền thất bại!"
                });
                return;
            }
        } catch (error) {
            console.log(error);
            message.error({
                content: "Cập nhật ngày nhận tiền thất bại!"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal 
                open={isModalOpen} 
                onCancel={handleCancel} 
                width={520} 
                centered
                title="SỬA NGÀY NHẬN TIỀN THỰC TẾ"
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <Button key="submit" type="primary" loading={loading}
                            style={{ 
                                background: "var(--primary-color)", 
                                paddingRight: 48, 
                                paddingLeft: 48, 
                                fontSize: 18,
                                fontWeight: 600,
                                paddingTop: 18, 
                                paddingBottom: 18,
                            }}
                            onClick={handleSave}
                        >
                            CẬP NHẬT
                        </Button>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-xl font-bold text-primary">Thông tin hóa đơn</p>
                </div>
                
                <div className="flex flex-row justify-center mt-6">
                    <Form<{paymentDate?: any}>
                        name="updatePaymentDateForm"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                    >
                        <div>
                            <div className="text-lg pb-4 mb-4 border-b border-gray-200">
                                <p><b>Mã hóa đơn: <span style={{color: "var(--primary-color)"}}>{data.codeString}</span></b></p>
                                <p><b>Hóa đơn tháng: <span style={{color: "var(--primary-color)"}}>{data.month}/{data.year}</span></b></p>
                                <p><b>Mã khách hàng: {data.waterUser?.code}</b></p>
                                <p><b>Tên khách hàng: </b>{data.waterUser?.name}</p>
                                <p><b>Giá trị hóa đơn: </b>{formatCurrencyPipeText(data.total)}</p>
                                <p><b>Ngày nhận tiền hiện tại: </b>
                                    {data.paymentDate ? dayjsPipe(data.paymentDate)?.format(DEF_DATETIME_FORMAT) : "Chưa có"}
                                </p>
                            </div>
                            
                            <Form.Item<{paymentDate?: any}>
                                label="Ngày nhận tiền thực tế mới"
                                name="paymentDate"
                                getValueProps={(i)=>({value: dayjsPipe(i)})}
                                rules={[{ required: true, message: 'Vui lòng chọn ngày nhận tiền!' }]}
                            >
                                <DatePicker 
                                    format={DEF_DATETIME_FORMAT} 
                                    showTime 
                                    style={{width: INPUT_WIDTH}}
                                    placeholder="Chọn ngày nhận tiền thực tế"
                                />
                            </Form.Item>
                        </div>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    );
};
